"use client";

import { useState, useEffect, useRef, useCallback } from 'react';

// Load architecture data from API endpoint
const loadArchitectureData = async (repositoryId) => {
  try {
    if (!repositoryId) {
      throw new Error('Repository ID is required');
    }
    
    const response = await fetch(`/api/repositories/${repositoryId}/architecture`);
    if (!response.ok) {
      throw new Error(`Failed to load architecture data: ${response.status}`);
    }
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to load architecture data');
    }
    
    const data = result.data;
    console.log('Loaded architecture data from API:', {
      repositoryId,
      nodes: data.nodes?.length || 0,
      edges: data.edges?.length || 0,
      packages: data.packages?.length || 0
    });
    return data;
  } catch (error) {
    console.error('Error loading architecture data:', error);
    throw error;
  }
};

export default function ArchitectureDiagram({ repositoryId, user, repository }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('dependencies');
  const [layoutMode, setLayoutMode] = useState('force');
  const [architectureData, setArchitectureData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [highlightedPath, setHighlightedPath] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedPackage, setFocusedPackage] = useState(null);
  const [nodePositions, setNodePositions] = useState(new Map());
  const [showAllLabels, setShowAllLabels] = useState(false);
  
  const diagramRef = useRef(null);
  const svgRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    // Only load architecture data when repositoryId is provided
    if (repositoryId) {
      loadRealArchitectureData();
    }
  }, [repositoryId, user]);

  useEffect(() => {
    if (architectureData) {
      filterAndLayoutData();
    }
  }, [architectureData, viewMode, focusedPackage, layoutMode]);

  const loadRealArchitectureData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Loading architecture data for repository:', repositoryId);
      const data = await loadArchitectureData(repositoryId);
      setArchitectureData(data);
    } catch (err) {
      console.error("Error loading architecture data:", err);
      setError("Failed to load architecture diagram");
    } finally {
      setIsLoading(false);
    }
  };

  // Find connected nodes centered on target nodes (including all upstream and downstream nodes)
  const findConnectedNodes = (targetNodeIds, edges, maxDepth = 1) => {
    const connected = new Set(targetNodeIds);
    
    // Build adjacency lists for directed graph
    const outgoingGraph = {};
    const incomingGraph = {};
    
    edges.forEach(edge => {
      // Build outgoing graph (call relationships)
      if (!outgoingGraph[edge.source]) outgoingGraph[edge.source] = [];
      outgoingGraph[edge.source].push(edge.target);
      
      // Build incoming graph (called by relationships)
      if (!incomingGraph[edge.target]) incomingGraph[edge.target] = [];
      incomingGraph[edge.target].push(edge.source);
    });
    
    // For each target node, use BFS to find all nodes in subgraph
    targetNodeIds.forEach(targetId => {
      const queue = [{ nodeId: targetId, depth: 0 }];
      const visited = new Set([targetId]);
      
      while (queue.length > 0) {
        const { nodeId, depth } = queue.shift();
        
        if (depth >= maxDepth) continue;
        
        // Add all nodes that call current node (upstream)
        const callers = incomingGraph[nodeId] || [];
        callers.forEach(callerId => {
          if (!visited.has(callerId)) {
            visited.add(callerId);
            connected.add(callerId);
            queue.push({ nodeId: callerId, depth: depth + 1 });
          }
        });
        
        // Add all nodes called by current node (downstream)
        const callees = outgoingGraph[nodeId] || [];
        callees.forEach(calleeId => {
          if (!visited.has(calleeId)) {
            visited.add(calleeId);
            connected.add(calleeId);
            queue.push({ nodeId: calleeId, depth: depth + 1 });
          }
        });
      }
    });
    
    return connected;
  };

  const filterAndLayoutData = () => {
    if (!architectureData) return;

    let nodes = [...architectureData.nodes];
    let edges = [...architectureData.edges];

    // 1. Package filtering
    if (focusedPackage) {
      nodes = nodes.filter(node => 
        node.package === focusedPackage || 
        node.package.startsWith(focusedPackage + '.')
      );
    }

    // 2. View mode filtering
    switch (viewMode) {
      case 'overview':
        // Show only modules and major classes
        nodes = nodes.filter(node => {
          if (node.type === 'module') return true;
          if (node.type === 'class' && (node.complexity > 5 || node.size > 100)) return true;
          return false;
        });
        break;
      
      case 'detailed':
        // Show all nodes but limit function count
        const functionNodes = nodes.filter(n => n.type === 'function');
        const importantFunctions = functionNodes
          .sort((a, b) => (b.complexity || 0) - (a.complexity || 0))
          .slice(0, 50);
        
        nodes = nodes.filter(n => 
          n.type !== 'function' || importantFunctions.includes(n)
        );
        break;
      
      case 'dependencies':
        // Show only nodes with dependencies
        const connectedIds = new Set();
        edges.forEach(edge => {
          if (edge.type === 'calls' || edge.type === 'imports') {
            connectedIds.add(edge.source);
            connectedIds.add(edge.target);
          }
        });
        nodes = nodes.filter(node => connectedIds.has(node.id));
        break;
    }

    // 3. Filter related edges
    const nodeIds = new Set(nodes.map(n => n.id));
    edges = edges.filter(edge => 
      nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );

    // 4. Apply layout algorithm
    const layoutNodes = applyLayout(nodes, edges);

    // 5. Initialize nodes and edges (no subgraph state)
    const nodesWithoutSubgraph = layoutNodes.map(node => ({
      ...node,
      isInSubgraph: false,
      isSubgraphActive: false
    }));

    const edgesWithoutSubgraph = edges.map(edge => ({
      ...edge,
      isInSubgraph: false,
      isSubgraphActive: false
    }));

    setFilteredData({ nodes: nodesWithoutSubgraph, edges: edgesWithoutSubgraph });
  };

  // Update subgraph state only, don't recalculate layout
  const updateSubgraphState = (targetNodeIds = [], isActive = false) => {
    if (!filteredData) return;

    const connectedNodeIds = isActive && targetNodeIds.length > 0 ? 
      findConnectedNodes(targetNodeIds, filteredData.edges) : 
      new Set();

    const nodesWithSubgraph = filteredData.nodes.map(node => ({
      ...node,
      isInSubgraph: connectedNodeIds.has(node.id),
      isSubgraphActive: isActive
    }));

    const edgesWithSubgraph = filteredData.edges.map(edge => ({
      ...edge,
      isInSubgraph: connectedNodeIds.has(edge.source) && connectedNodeIds.has(edge.target),
      isSubgraphActive: isActive
    }));

    setFilteredData({ nodes: nodesWithSubgraph, edges: edgesWithSubgraph });
    return connectedNodeIds;
  };

  const applyLayout = (nodes, edges) => {
    switch (layoutMode) {
      case 'force':
        return applyForceLayout(nodes, edges);
      case 'cluster':
        return applyClusterLayout(nodes, edges);
      default:
        return applyForceLayout(nodes, edges);
    }
  };

  // Force-directed layout - gold standard for graph visualization
  const applyForceLayout = (nodes, edges) => {
    // Use fixed coordinate system for consistency
    const width = 800;
    const height = 600;
    const margin = 80;
    
    // Initialize positions with better distribution strategy
    const positions = new Map();
    nodes.forEach((node, index) => {
      if (nodePositions.has(node.id)) {
        // Use saved positions
        positions.set(node.id, { ...nodePositions.get(node.id) });
      } else {
        // Use grid initialization + random offset to avoid clustering from pure randomness
        const gridSize = Math.ceil(Math.sqrt(nodes.length));
        const cellWidth = (width - 2 * margin) / gridSize;
        const cellHeight = (height - 2 * margin) / gridSize;
        
        const gridX = index % gridSize;
        const gridY = Math.floor(index / gridSize);
        
        positions.set(node.id, {
          x: margin + gridX * cellWidth + cellWidth / 2 + (Math.random() - 0.5) * cellWidth * 0.5,
          y: margin + gridY * cellHeight + cellHeight / 2 + (Math.random() - 0.5) * cellHeight * 0.5,
          vx: 0,
          vy: 0
        });
      }
    });

    // Force-directed algorithm parameters - fixed for consistency
    const params = {
      repulsion: 2000,
      attraction: 0.05,
      damping: 0.85,
      iterations: 150,
      minDistance: 30
    };

    // Run force-directed algorithm
    for (let i = 0; i < params.iterations; i++) {
      // Repulsion force - improved calculation
      nodes.forEach(nodeA => {
        const posA = positions.get(nodeA.id);
        nodes.forEach(nodeB => {
          if (nodeA.id === nodeB.id) return;
          const posB = positions.get(nodeB.id);
          
          const dx = posA.x - posB.x;
          const dy = posA.y - posB.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          
          // Avoid division by zero and too small distances
          if (distance < params.minDistance) {
            distance = params.minDistance;
            // Add random offset if too close
            const randomAngle = Math.random() * 2 * Math.PI;
            const randomForce = params.repulsion * 0.1;
            posA.vx += Math.cos(randomAngle) * randomForce;
            posA.vy += Math.sin(randomAngle) * randomForce;
          }
          
          const force = params.repulsion / (distance * distance);
          const fx = (dx / distance) * force;
          const fy = (dy / distance) * force;
          
          posA.vx += fx;
          posA.vy += fy;
        });
      });

      // Attraction force (based on edges)
      edges.forEach(edge => {
        const posSource = positions.get(edge.source);
        const posTarget = positions.get(edge.target);
        
        if (!posSource || !posTarget) return;
        
        const dx = posTarget.x - posSource.x;
        const dy = posTarget.y - posSource.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        const force = distance * params.attraction;
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;
        
        posSource.vx += fx;
        posSource.vy += fy;
        posTarget.vx -= fx;
        posTarget.vy -= fy;
      });

      // Center gravity - prevent nodes from flying away
      const centerX = width / 2;
      const centerY = height / 2;
      positions.forEach(pos => {
        const dx = centerX - pos.x;
        const dy = centerY - pos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 0) {
          const centerForce = 0.01;
          pos.vx += (dx / distance) * centerForce;
          pos.vy += (dy / distance) * centerForce;
        }
      });

      // Update positions
      positions.forEach(pos => {
        pos.x += pos.vx;
        pos.y += pos.vy;
        pos.vx *= params.damping;
        pos.vy *= params.damping;
        
        // Improved boundary constraints - use soft boundaries
        const softMargin = margin * 0.8;
        if (pos.x < softMargin) {
          pos.x = softMargin;
          pos.vx = Math.abs(pos.vx) * 0.5;
        }
        if (pos.x > width - softMargin) {
          pos.x = width - softMargin;
          pos.vx = -Math.abs(pos.vx) * 0.5;
        }
        if (pos.y < softMargin) {
          pos.y = softMargin;
          pos.vy = Math.abs(pos.vy) * 0.5;
        }
        if (pos.y > height - softMargin) {
          pos.y = height - softMargin;
          pos.vy = -Math.abs(pos.vy) * 0.5;
        }
      });
    }

    // Update node position state
    setNodePositions(new Map(positions));

    return nodes.map(node => ({
      ...node,
      x: positions.get(node.id).x,
      y: positions.get(node.id).y
    }));
  };

  // Cluster layout - cluster by package/module
  const applyClusterLayout = (nodes, edges) => {
    const width = 800;
    const height = 600;
    
    // Group by package
    const packages = [...new Set(nodes.map(n => n.package))];
    const clusterRadius = 120;
    const centerRadius = Math.min(width, height) * 0.3;
    
    return nodes.map(node => {
      const pkg = node.package || 'root';
      const packageIndex = packages.indexOf(pkg);
      const packageAngle = (packageIndex / packages.length) * 2 * Math.PI;
      
      // Package center position
      const clusterCenterX = width / 2 + Math.cos(packageAngle) * centerRadius;
      const clusterCenterY = height / 2 + Math.sin(packageAngle) * centerRadius;
      
      // Node position within package
      const nodesInPackage = nodes.filter(n => n.package === pkg);
      const nodeIndex = nodesInPackage.indexOf(node);
      const nodeAngle = (nodeIndex / nodesInPackage.length) * 2 * Math.PI;
      const nodeRadius = Math.min(clusterRadius, nodesInPackage.length * 8);
      
      return {
        ...node,
        x: clusterCenterX + Math.cos(nodeAngle) * nodeRadius,
        y: clusterCenterY + Math.sin(nodeAngle) * nodeRadius
      };
    });
  };

  // Calculate node importance score
  const calculateNodeImportance = (node, edges) => {
    let score = 0;
    
    // Base score by node type
    const typeScores = {
      module: 50,
      class: 30,
      constructor: 25,
      function: 10
    };
    score += typeScores[node.type] || 5;
    
    // Connection degree score: in-degree and out-degree
    const incomingEdges = edges.filter(e => e.target === node.id);
    const outgoingEdges = edges.filter(e => e.source === node.id);
    score += incomingEdges.length * 3;
    score += outgoingEdges.length * 2;
    
    // Complexity score
    if (node.complexity) {
      score += Math.min(node.complexity * 2, 30);
    }
    
    // Code size score
    if (node.size) {
      score += Math.min(Math.log(node.size + 1) * 5, 25);
    }
    
    // Parameter count score (function complexity indicator)
    if (node.parameters_count) {
      score += Math.min(node.parameters_count * 1.5, 15);
    }
    
    // Documentation score (documented nodes are more important)
    if (node.documentation && node.documentation.length > 50) {
      score += 10;
    }
    
    // Package level score (top-level packages are more important)
    const packageDepth = (node.package || '').split('.').length;
    score += Math.max(10 - packageDepth, 0);
    
    return score;
  };

  // Determine if node should be displayed fully
  const shouldShowFullNode = (node, allNodes, edges, searchTerm, selectedNode, highlightedPath) => {
    // Always show cases
    if (selectedNode?.id === node.id) return true;
    if (highlightedPath.includes(node.id)) return true;
    if (searchTerm && (
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.package.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.file_path.toLowerCase().includes(searchTerm.toLowerCase())
    )) return true;
    
    // If subgraph is active, only show nodes in subgraph
    if (node.isSubgraphActive && node.isInSubgraph) return true;
    if (node.isSubgraphActive && !node.isInSubgraph) return false;
    
    // Decide based on importance
    const importance = calculateNodeImportance(node, edges);
    
    // Dynamic threshold based on total node count
    const totalNodes = allNodes.length;
    let threshold;
    
    if (totalNodes > 1000) {
      threshold = 80;
    } else if (totalNodes > 500) {
      threshold = 60;
    } else if (totalNodes > 200) {
      threshold = 40;
    } else {
      threshold = 20;
    }
    
    return importance >= threshold;
  };

  // Get node display size - distinguish between full nodes and small dots
  const getNodeSize = (node, isSelected = false, isFullNode = true) => {
    if (!isFullNode) {
      // Small dot mode: fixed small size
      return isSelected ? 4 : 2;
    }
    
    // Full node mode: original logic
    const baseSize = 8;
    const complexity = node.complexity || 1;
    const size = node.size || 1;
    
    // Importance score
    const importance = Math.log(complexity + 1) + Math.log(size + 1);
    const sizeMultiplier = Math.min(importance * 2, 20);
    
    return baseSize + sizeMultiplier + (isSelected ? 6 : 0);
  };

  // Get node color based on type and importance
  const getNodeColor = (node, isHighlighted = false, isSelected = false) => {
    if (isSelected) return '#EF4444';
    
    const colors = {
      module: isHighlighted ? '#1E40AF' : '#3B82F6',
      class: isHighlighted ? '#047857' : '#10B981',
      function: isHighlighted ? '#D97706' : '#F59E0B',
      constructor: isHighlighted ? '#7C3AED' : '#8B5CF6'
    };
    
    return colors[node.type] || (isHighlighted ? '#374151' : '#6B7280');
  };

  // Get edge style
  const getEdgeStyle = (edge, isHighlighted = false) => {
    const styles = {
      calls: { color: isHighlighted ? '#EF4444' : '#6B7280', width: isHighlighted ? 1.5 : 0.8 },
      imports: { color: isHighlighted ? '#10B981' : '#9CA3AF', width: isHighlighted ? 1 : 0.5 },
      contains: { color: isHighlighted ? '#8B5CF6' : '#D1D5DB', width: isHighlighted ? 1 : 0.5 },
      extends: { color: isHighlighted ? '#F59E0B' : '#9CA3AF', width: isHighlighted ? 1.5 : 0.8 },
      implements: { color: isHighlighted ? '#EC4899' : '#9CA3AF', width: isHighlighted ? 1 : 0.5 }
    };
    
    return styles[edge.type] || { color: '#9CA3AF', width: 0.5 };
  };

  // Handle node click
  const handleNodeClick = (node) => {
    if (selectedNode?.id === node.id) {
      // If clicking already selected node, clear selection
      setSelectedNode(null);
      setHighlightedPath([]);
      // Clear subgraph state
      updateSubgraphState([], false);
    } else {
      // Select new node
      setSelectedNode(node);
      
      console.log(`Clicked node "${node.name}" (ID: ${node.id})`);
      
      // Highlight connected nodes for this specific node only
      const connected = new Set([node.id]);
      filteredData.edges.forEach(edge => {
        if (edge.source === node.id) connected.add(edge.target);
        if (edge.target === node.id) connected.add(edge.source);
      });
      setHighlightedPath([...connected]);
      
      // Set subgraph state to show only this node and its connections
      updateSubgraphState([node.id], true);
    }
  };

  // Handle search box changes
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    
    if (newSearchTerm.trim() && filteredData) {
      // Search for matching nodes and auto-select the best match
      const searchLower = newSearchTerm.toLowerCase();
      const matchingNodes = filteredData.nodes.filter(node => 
        node.name.toLowerCase().includes(searchLower) ||
        node.package.toLowerCase().includes(searchLower) ||
        node.file_path.toLowerCase().includes(searchLower)
      );
      
      if (matchingNodes.length > 0) {
        // Smart sorting: priority = exact match > name starts match > importance > contains match
        const sortedMatches = matchingNodes.sort((a, b) => {
          const aNameLower = a.name.toLowerCase();
          const bNameLower = b.name.toLowerCase();
          
          // 1. Exact match priority
          const aExactMatch = aNameLower === searchLower ? 1000 : 0;
          const bExactMatch = bNameLower === searchLower ? 1000 : 0;
          
          // 2. Name starts match
          const aStartsMatch = aNameLower.startsWith(searchLower) ? 500 : 0;
          const bStartsMatch = bNameLower.startsWith(searchLower) ? 500 : 0;
          
          // 3. Importance score
          const aImportance = calculateNodeImportance(a, filteredData.edges);
          const bImportance = calculateNodeImportance(b, filteredData.edges);
          
          // 4. Name length (shorter names might be more important)
          const aLengthScore = Math.max(50 - a.name.length, 0);
          const bLengthScore = Math.max(50 - b.name.length, 0);
          
          const aScore = aExactMatch + aStartsMatch + aImportance + aLengthScore;
          const bScore = bExactMatch + bStartsMatch + bImportance + bLengthScore;
          
          return bScore - aScore;
        });
        
        const bestMatch = sortedMatches[0];
        
        // Always update selection for search
        setSelectedNode(bestMatch);
        
        // Highlight connected nodes for the best match
        const connected = new Set([bestMatch.id]);
        filteredData.edges.forEach(edge => {
          if (edge.source === bestMatch.id) connected.add(edge.target);
          if (edge.target === bestMatch.id) connected.add(edge.source);
        });
        setHighlightedPath([...connected]);
        
        console.log(`Search "${newSearchTerm}": auto-selected "${bestMatch.name}" (${matchingNodes.length} matches)`);
      } else {
        // Clear selection when no matches
        setSelectedNode(null);
        setHighlightedPath([]);
      }
    } else {
      // Clear selection when search box is empty
      setSelectedNode(null);
      setHighlightedPath([]);
    }
  };

  // Handle package click
  const handlePackageClick = (packageName) => {
    setFocusedPackage(focusedPackage === packageName ? null : packageName);
  };

  // Export functionality
  const handleExport = (format) => {
    if (!svgRef.current) return;
    
    const svgElement = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `architecture-${viewMode}-${Date.now()}.${format}`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  };

  // Reset view
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setSelectedNode(null);
    setSelectedEdge(null);
    setHighlightedPath([]);
    
    // Only recalculate layout if there are filter conditions
    const hasFilters = searchTerm || focusedPackage;
    
    setSearchTerm('');
    setFocusedPackage(null);
    
    if (hasFilters) {
      // Recalculate layout when there are filter conditions
      setNodePositions(new Map());
      setTimeout(() => filterAndLayoutData(), 0);
    } else {
      // Only clear subgraph effects when no filter conditions
      updateSubgraphState([], false);
    }
  };

  // Detect text label overlap
  const checkTextOverlap = (nodes, zoom) => {
    const labelSpacing = 25 / zoom;
    const visibleLabels = new Set();
    
    // Sort nodes by importance (selected > highlighted > size > complexity)
    const sortedNodes = [...nodes].sort((a, b) => {
      const aSelected = selectedNode?.id === a.id ? 1000 : 0;
      const bSelected = selectedNode?.id === b.id ? 1000 : 0;
      const aHighlighted = highlightedPath.includes(a.id) ? 500 : 0;
      const bHighlighted = highlightedPath.includes(b.id) ? 500 : 0;
      const aImportance = aSelected + aHighlighted + (a.complexity || 0) + (a.size || 0);
      const bImportance = bSelected + bHighlighted + (b.complexity || 0) + (b.size || 0);
      return bImportance - aImportance;
    });
    
    // Check if each node's label overlaps with already displayed labels
    sortedNodes.forEach(node => {
      const nodeSize = getNodeSize(node, selectedNode?.id === node.id);
      const labelY = node.y + nodeSize + 14;
      
      let canShow = true;
      for (const visibleNode of visibleLabels) {
        const distance = Math.sqrt(
          Math.pow(node.x - visibleNode.x, 2) + 
          Math.pow(labelY - visibleNode.labelY, 2)
        );
        
        if (distance < labelSpacing) {
          canShow = false;
          break;
        }
      }
      
      if (canShow) {
        visibleLabels.add({ ...node, labelY });
      }
    });
    
    return visibleLabels;
  };

  // Separate search effect handling
  useEffect(() => {
    if (!filteredData) return;
    
    if (searchTerm) {
      // When searching, update subgraph state to show all matching nodes
      const searchLower = searchTerm.toLowerCase();
      const directMatches = filteredData.nodes.filter(node => 
        node.name.toLowerCase().includes(searchLower) ||
        node.package.toLowerCase().includes(searchLower) ||
        node.file_path.toLowerCase().includes(searchLower)
      );
      
      if (directMatches.length > 0) {
        const directMatchIds = directMatches.map(n => n.id);
        const connectedNodeIds = findConnectedNodes(directMatchIds, filteredData.edges);
        
        // Check if update is needed (avoid infinite loop)
        const needsUpdate = !filteredData.nodes.some(n => n.isSubgraphActive) || 
          filteredData.nodes.some(n => n.isInSubgraph !== connectedNodeIds.has(n.id));
        
        if (needsUpdate) {
          // Update subgraph state but keep existing layout
          const nodesWithSubgraph = filteredData.nodes.map(node => ({
            ...node,
            isInSubgraph: connectedNodeIds.has(node.id),
            isSubgraphActive: true
          }));

          const edgesWithSubgraph = filteredData.edges.map(edge => ({
            ...edge,
            isInSubgraph: connectedNodeIds.has(edge.source) && connectedNodeIds.has(edge.target),
            isSubgraphActive: true
          }));

          setFilteredData({ nodes: nodesWithSubgraph, edges: edgesWithSubgraph });
        }
      }
    } else {
      // When clearing search, clear subgraph state
      const needsUpdate = filteredData.nodes.some(n => n.isSubgraphActive);
      
      if (needsUpdate) {
        const nodesWithoutSubgraph = filteredData.nodes.map(node => ({
          ...node,
          isInSubgraph: false,
          isSubgraphActive: false
        }));

        const edgesWithoutSubgraph = filteredData.edges.map(edge => ({
          ...edge,
          isInSubgraph: false,
          isSubgraphActive: false
        }));

        setFilteredData({ nodes: nodesWithoutSubgraph, edges: edgesWithoutSubgraph });
      }
    }
  }, [searchTerm]);

  if (!repositoryId) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto text-[#57606A] dark:text-gray-400">
            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-[#24292F] dark:text-white">Select a Repository</h3>
            <p className="text-[#57606A] dark:text-gray-400">
              Choose a repository from the sidebar to view its architecture diagram.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2188FF] mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-[#2188FF] rounded-full animate-pulse"></div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-[#57606A] dark:text-gray-400 font-medium">Analyzing codebase architecture...</p>
            <p className="text-xs text-[#57606A] dark:text-gray-400">Building knowledge graph</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-20 h-20 mx-auto text-red-500">
            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-[#24292F] dark:text-white">Analysis Failed</h3>
            <p className="text-[#57606A] dark:text-gray-400">{error}</p>
          </div>
          <button
            onClick={loadRealArchitectureData}
            className="px-6 py-3 bg-[#2188FF] hover:bg-[#0969DA] text-white rounded-lg transition-colors font-medium"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!filteredData || filteredData.nodes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto text-[#57606A] dark:text-gray-400">
            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-[#24292F] dark:text-white">No Data to Display</h3>
            <p className="text-[#57606A] dark:text-gray-400">
              {searchTerm || focusedPackage ? 'No nodes match your current filters.' : 'No architectural data available.'}
            </p>
          </div>
          {(searchTerm || focusedPackage) && (
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm bg-[#F6F8FA] dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-[#24292F] dark:text-white rounded-md transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  // Get package list for filtering
  const packages = architectureData.packages || [];
  
  // Create package ID to package information mapping
  const packageMap = new Map();
  packages.forEach(pkg => {
    packageMap.set(pkg.id, pkg);
  });
  
  // Get actual package IDs used in node
  const usedPackageIds = [...new Set(architectureData.nodes.map(n => n.package))].sort();
  
  // Filter out actual packages used and sort by level
  const usedPackages = usedPackageIds
    .map(pkgId => packageMap.get(pkgId))
    .filter(pkg => pkg) // Filter out packages not found
    .sort((a, b) => {
      // Sort by level depth then by name
      const depthA = (a.full_name.match(/\./g) || []).length;
      const depthB = (b.full_name.match(/\./g) || []).length;
      if (depthA !== depthB) return depthA - depthB;
      return a.full_name.localeCompare(b.full_name);
    });

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      {/* Control panel */}
      <div className="flex-shrink-0 p-3 border-b border-[#D0D7DE]/30 dark:border-gray-700 bg-[#F6F8FA] dark:bg-gray-900">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {/* Title and package selection */}
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-[#24292F] dark:text-white">
              {repository?.full_name || architectureData?.metadata?.project_name || "Packages"}
            </h3>
            <select
              value={focusedPackage || ''}
              onChange={(e) => handlePackageClick(e.target.value || null)}
              className="text-xs px-2 py-1 border border-[#D0D7DE] dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#24292F] dark:text-white"
            >
              <option value="">All Packages</option>
              {usedPackages.map(pkg => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Search box */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="text-xs px-2 py-1 border border-[#D0D7DE] dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#24292F] dark:text-white w-32"
            />
            {searchTerm && (
              <button
                onClick={() => handleSearchChange('')}
                className="text-xs text-[#57606A] dark:text-gray-400 hover:text-[#24292F] dark:hover:text-white"
              >
                ✕
              </button>
            )}
          </div>

          {/* Layout mode */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-[#57606A] dark:text-gray-400">Layout:</span>
            <select
              value={layoutMode}
              onChange={(e) => setLayoutMode(e.target.value)}
              className="text-xs px-2 py-1 border border-[#D0D7DE] dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#24292F] dark:text-white"
            >
              <option value="force">Force</option>
              <option value="cluster">Cluster</option>
            </select>
          </div>

          {/* View mode */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-[#57606A] dark:text-gray-400">View:</span>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="text-xs px-2 py-1 border border-[#D0D7DE] dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#24292F] dark:text-white"
            >
              <option value="dependencies">Deps</option>
              <option value="overview">Overview</option>
              <option value="detailed">Detailed</option>
            </select>
          </div>

          {/* Label display mode */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-[#57606A] dark:text-gray-400">Labels:</span>
            <button
              onClick={() => setShowAllLabels(!showAllLabels)}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                showAllLabels 
                  ? 'bg-[#2188FF] text-white' 
                  : 'bg-white dark:bg-gray-700 border border-[#D0D7DE] dark:border-gray-600 text-[#24292F] dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              {showAllLabels ? 'All' : 'Smart'}
            </button>
          </div>

          {/* Statistics */}
          <div className="text-xs text-[#57606A] dark:text-gray-400 ml-auto">
            {(() => {
              const fullNodes = filteredData.nodes.filter(node => 
                shouldShowFullNode(node, filteredData.nodes, filteredData.edges, searchTerm, selectedNode, highlightedPath)
              ).length;
              const totalNodes = filteredData.nodes.length;
              const smallDots = totalNodes - fullNodes;
              
              return (
                <>
                  {fullNodes}n
                  {smallDots > 0 && <span className="text-[#9CA3AF]"> (+{smallDots})</span>}
                  , {filteredData.edges.length}e
                </>
              );
            })()}
            {focusedPackage && (
              <span className="ml-2 px-2 py-1 bg-[#2188FF]/10 text-[#2188FF] rounded">
                {packageMap.get(focusedPackage)?.full_name || focusedPackage}
              </span>
            )}
          </div>

          {/* Reset button */}
          <button
            onClick={handleReset}
            className="text-xs px-2 py-1 bg-white dark:bg-gray-700 border border-[#D0D7DE] dark:border-gray-600 rounded text-[#24292F] dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Reset
          </button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-2 pt-2 border-t border-[#D0D7DE]/20 dark:border-gray-700">
          <span className="text-xs text-[#57606A] dark:text-gray-400 font-medium">Legend:</span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-[#3B82F6]"></div>
              <span className="text-xs text-[#57606A] dark:text-gray-400">Module</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
              <span className="text-xs text-[#57606A] dark:text-gray-400">Class</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
              <span className="text-xs text-[#57606A] dark:text-gray-400">Function</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-[#8B5CF6]"></div>
              <span className="text-xs text-[#57606A] dark:text-gray-400">Constructor</span>
            </div>
          </div>
          
          {/* Edge type legend */}
          <div className="flex items-center gap-3 ml-4 pl-4 border-l border-[#D0D7DE]/20 dark:border-gray-700">
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 bg-[#6B7280]"></div>
              <span className="text-xs text-[#57606A] dark:text-gray-400">Calls</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 bg-[#9CA3AF] opacity-70"></div>
              <span className="text-xs text-[#57606A] dark:text-gray-400">Imports</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area - divided into chart and chat box */}
      <div className="flex-1 flex">
        {/* Left chart area - 70% */}
        <div className="relative overflow-hidden" style={{ flex: '0 0 70%' }}>
          <div 
            ref={diagramRef}
            className="w-full h-full bg-gradient-to-br from-[#FAFBFC] to-[#F6F8FA] dark:from-gray-900 dark:to-gray-800"
          >
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              viewBox="0 0 800 600"
              className="w-full h-full"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#6B7280"
                  />
                </marker>
                <marker
                  id="arrowhead-highlighted"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#EF4444"
                  />
                </marker>
                <marker
                  id="arrowhead-hidden"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon
                    points="0 0, 10 3.5, 0 7"
                    fill="#6B7280"
                    fillOpacity="0.05"
                  />
                </marker>
                
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                {/* Edges */}
                {filteredData.edges.map((edge) => {
                  const sourceNode = filteredData.nodes.find(n => n.id === edge.source);
                  const targetNode = filteredData.nodes.find(n => n.id === edge.target);
                  
                  if (!sourceNode || !targetNode) return null;
                  
                  const isHighlighted = highlightedPath.includes(edge.source) && highlightedPath.includes(edge.target);
                  const style = getEdgeStyle(edge, isHighlighted);
                  
                  // Calculate opacity: if subgraph is active and edge is not in subgraph, then semi-transparent
                  const opacity = edge.isSubgraphActive && !edge.isInSubgraph ? 0.02 : (isHighlighted ? 0.9 : 0.6);
                  
                  // Choose appropriate arrow marker
                  let markerEnd;
                  if (isHighlighted) {
                    markerEnd = "url(#arrowhead-highlighted)";
                  } else if (edge.isSubgraphActive && !edge.isInSubgraph) {
                    markerEnd = "url(#arrowhead-hidden)";
                  } else {
                    markerEnd = "url(#arrowhead)";
                  }
                  
                  return (
                    <line
                      key={edge.id}
                      x1={sourceNode.x}
                      y1={sourceNode.y}
                      x2={targetNode.x}
                      y2={targetNode.y}
                      stroke={style.color}
                      strokeWidth={style.width}
                      strokeOpacity={opacity}
                      markerEnd={markerEnd}
                      className="transition-all duration-300"
                    />
                  );
                })}
                
                {(() => {
                  // Calculate visible text labels
                  const visibleLabels = showAllLabels ? 
                    new Set(filteredData.nodes) : 
                    checkTextOverlap(filteredData.nodes, zoom);
                  
                  return (
                    <>
                      {/* Nodes */}
                      {filteredData.nodes.map((node) => {
                        const isSelected = selectedNode?.id === node.id;
                        const isHighlighted = highlightedPath.includes(node.id);
                        const isFullNode = shouldShowFullNode(node, filteredData.nodes, filteredData.edges, searchTerm, selectedNode, highlightedPath);
                        const nodeSize = getNodeSize(node, isSelected, isFullNode);
                        const nodeColor = getNodeColor(node, isHighlighted, isSelected);
                        
                        // Calculate opacity: if subgraph is active and node is not in subgraph, then semi-transparent
                        const opacity = node.isSubgraphActive && !node.isInSubgraph ? 0.03 : 1;
                        
                        // Check if there are visible labels (only show labels for full nodes)
                        const isLabelVisible = isFullNode && (showAllLabels || Array.from(checkTextOverlap(filteredData.nodes, zoom)).some(v => v.id === node.id));
                        
                        return (
                          <g key={node.id} opacity={opacity}>
                            <circle
                              cx={node.x}
                              cy={node.y}
                              r={nodeSize}
                              fill={isFullNode ? nodeColor : (isSelected ? nodeColor : '#9CA3AF')}
                              stroke={isSelected ? "#EF4444" : isHighlighted ? "#2188FF" : (isFullNode ? "white" : "none")}
                              strokeWidth={isSelected ? 3 : isHighlighted ? 2 : (isFullNode ? 1 : 0)}
                              className="cursor-pointer hover:opacity-80 transition-all duration-300"
                              filter={isSelected ? "url(#glow)" : undefined}
                              onClick={() => handleNodeClick(node)}
                              onMouseEnter={(e) => {
                                // If no visible labels, show hover label
                                if (!isLabelVisible) {
                                  const hoverText = document.getElementById(`hover-text-${node.id}`);
                                  if (hoverText) hoverText.style.opacity = '1';
                                }
                              }}
                              onMouseLeave={(e) => {
                                // Hide hover label
                                if (!isLabelVisible) {
                                  const hoverText = document.getElementById(`hover-text-${node.id}`);
                                  if (hoverText) hoverText.style.opacity = '0';
                                }
                              }}
                            />
                          </g>
                        );
                      })}
                      
                      {/* Text labels */}
                      {Array.from(visibleLabels).map((node) => {
                        const isSelected = selectedNode?.id === node.id;
                        const isHighlighted = highlightedPath.includes(node.id);
                        const isFullNode = shouldShowFullNode(node, filteredData.nodes, filteredData.edges, searchTerm, selectedNode, highlightedPath);
                        const nodeSize = getNodeSize(node, isSelected, isFullNode);
                        const opacity = node.isSubgraphActive && !node.isInSubgraph ? 0.03 : 1;
                        
                        // Only show labels for full nodes
                        if (!isFullNode) return null;
                        
                        return (
                          <text
                            key={`label-${node.id}`}
                            x={node.x}
                            y={node.y + nodeSize + 14}
                            textAnchor="middle"
                            className="text-xs fill-current text-[#24292F] dark:text-white pointer-events-none font-medium"
                            style={{ 
                              fontSize: isSelected ? '12px' : isHighlighted ? '11px' : showAllLabels ? '8px' : '10px',
                              fontWeight: isSelected ? 'bold' : isHighlighted ? '600' : 'normal'
                            }}
                            opacity={opacity}
                          >
                            {(() => {
                              const maxLength = showAllLabels ? 8 : (isSelected ? 20 : 12);
                              return node.name.length > maxLength ? 
                                `${node.name.substring(0, maxLength)}...` : 
                                node.name;
                            })()}
                          </text>
                        );
                      })}
                      
                      {/* Hover label - only show in smart mode */}
                      {!showAllLabels && filteredData.nodes.map((node) => {
                        const isSelected = selectedNode?.id === node.id;
                        const isFullNode = shouldShowFullNode(node, filteredData.nodes, filteredData.edges, searchTerm, selectedNode, highlightedPath);
                        const nodeSize = getNodeSize(node, isSelected, isFullNode);
                        const opacity = node.isSubgraphActive && !node.isInSubgraph ? 0.03 : 1;
                        const isLabelVisible = isFullNode && Array.from(visibleLabels).some(v => v.id === node.id);
                        
                        // Only add hover label for nodes without visible labels (including small dot nodes)
                        if (isLabelVisible) return null;
                        
                        return (
                          <g key={`hover-${node.id}`} opacity={opacity}>
                            <text
                              id={`hover-text-${node.id}`}
                              x={node.x}
                              y={node.y + nodeSize + 14}
                              textAnchor="middle"
                              className="text-xs fill-current text-[#24292F] dark:text-white pointer-events-none font-medium opacity-0 transition-opacity duration-200"
                              style={{ fontSize: isFullNode ? '9px' : '8px' }}
                            >
                              {node.name.length > 10 ? `${node.name.substring(0, 10)}...` : node.name}
                            </text>
                          </g>
                        );
                      })}
                    </>
                  );
                })()}
              </g>
            </svg>
          </div>
        </div>

        {/* Right chat box area - 30% */}
        <div className="flex-1 p-4 border-l border-[#D0D7DE]/30 dark:border-gray-700">
          {/* Node details panel */}
          {selectedNode ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-[#24292F] dark:text-white">Node Details</h4>
                <div className="flex items-center gap-2">
                  {searchTerm && selectedNode?.name.toLowerCase().includes(searchTerm.toLowerCase()) && (
                    <span className="text-xs px-2 py-1 bg-[#2188FF]/10 text-[#2188FF] rounded">
                      Found by search
                    </span>
                  )}
                  <button
                    onClick={() => setSelectedNode(null)}
                    className="text-[#57606A] dark:text-gray-400 hover:text-[#24292F] dark:hover:text-white"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-[#24292F] dark:text-white">Name:</span>
                  <p className="text-[#57606A] dark:text-gray-400 mt-1">{selectedNode.name}</p>
                </div>
                
                <div>
                  <span className="font-medium text-[#24292F] dark:text-white">Type:</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    selectedNode.type === 'module' ? 'bg-blue-100 text-blue-800' :
                    selectedNode.type === 'class' ? 'bg-green-100 text-green-800' :
                    selectedNode.type === 'function' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {selectedNode.type}
                  </span>
                </div>
                
                <div>
                  <span className="font-medium text-[#24292F] dark:text-white">Package:</span>
                  <p className="text-[#57606A] dark:text-gray-400 mt-1 font-mono text-xs">
                    {packageMap.get(selectedNode.package)?.full_name || selectedNode.package}
                  </p>
                </div>
                
                <div>
                  <span className="font-medium text-[#24292F] dark:text-white">File:</span>
                  <p className="text-[#57606A] dark:text-gray-400 mt-1 font-mono text-xs">
                    {selectedNode.file_path}
                  </p>
                </div>
                
                {selectedNode.line_number && (
                  <div>
                    <span className="font-medium text-[#24292F] dark:text-white">Line:</span>
                    <span className="ml-2 text-[#57606A] dark:text-gray-400">{selectedNode.line_number}</span>
                  </div>
                )}
                
                {selectedNode.complexity && (
                  <div>
                    <span className="font-medium text-[#24292F] dark:text-white">Complexity:</span>
                    <span className="ml-2 text-[#57606A] dark:text-gray-400">{selectedNode.complexity}</span>
                  </div>
                )}
                
                {selectedNode.size && (
                  <div>
                    <span className="font-medium text-[#24292F] dark:text-white">Size:</span>
                    <span className="ml-2 text-[#57606A] dark:text-gray-400">{selectedNode.size} lines</span>
                  </div>
                )}
                
                {selectedNode.parameters_count !== undefined && (
                  <div>
                    <span className="font-medium text-[#24292F] dark:text-white">Parameters:</span>
                    <span className="ml-2 text-[#57606A] dark:text-gray-400">{selectedNode.parameters_count}</span>
                  </div>
                )}
                
                {selectedNode.documentation && (
                  <div>
                    <span className="font-medium text-[#24292F] dark:text-white">Documentation:</span>
                    <p className="text-[#57606A] dark:text-gray-400 mt-1 text-xs">
                      {selectedNode.documentation.length > 200 
                        ? `${selectedNode.documentation.substring(0, 200)}...`
                        : selectedNode.documentation
                      }
                    </p>
                  </div>
                )}
                
                {selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 && (
                  <div>
                    <span className="font-medium text-[#24292F] dark:text-white">Metadata:</span>
                    <div className="mt-1 space-y-1">
                      {Object.entries(selectedNode.metadata).map(([key, value]) => (
                        <div key={key} className="text-xs">
                          <span className="text-[#57606A] dark:text-gray-400">{key}:</span>
                          <span className="ml-2 text-[#24292F] dark:text-white">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Connection information */}
              <div className="pt-4 border-t border-[#D0D7DE]/30 dark:border-gray-700">
                <h5 className="font-medium text-[#24292F] dark:text-white mb-2">Connections</h5>
                <div className="space-y-2 text-xs">
                  {(() => {
                    const incoming = filteredData.edges.filter(e => e.target === selectedNode.id);
                    const outgoing = filteredData.edges.filter(e => e.source === selectedNode.id);
                    
                    return (
                      <>
                        <div>
                          <span className="text-[#57606A] dark:text-gray-400">Incoming:</span>
                          <span className="ml-2 text-[#24292F] dark:text-white">{incoming.length}</span>
                        </div>
                        <div>
                          <span className="text-[#57606A] dark:text-gray-400">Outgoing:</span>
                          <span className="ml-2 text-[#24292F] dark:text-white">{outgoing.length}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-center">
              <div className="space-y-2">
                <div className="w-12 h-12 mx-auto text-[#57606A] dark:text-gray-400">
                  <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-[#57606A] dark:text-gray-400">
                  Click a node to view details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 